import React, { useEffect, useRef } from 'react';
import {
  useHamburgerMenuState,
  useStickyHeader,
  useCityPickerState,
  useLoyaltyAccount,
  useFavoriteAddress,
} from 'components/header/hooks';
import { HeaderModelData } from 'types/model';
import { useIsCheckout } from 'components/header/hooks/use-is-checkout';
import { MenuMobile } from '../menu-mobile';
import { headerFullScreenClassName, isVisible } from './styles';
import { HeaderContent } from './header-content';
import { withContextSetup, withModel } from '../hocs';
import { MainMenu } from '../main-menu';
import { RegionsMenu } from '../regions-menu';
import { State } from 'store/types';

import { useStoreon } from 'storeon/react';
import { isEnableLoyaltyRegistrationBanner, isFavoriteAddressEnabled, isUserAuth } from 'selectors';
import { LoyaltyRegistrationModal } from 'components/registration/registration';
import { useAutoShowLoyaltyRegistrationModal } from '../../hooks/use-auto-show-loyalty-registration-modal';
import { DeviceType } from 'types';
import { StickyContainer } from './sticky-container';
import { ExternalDependencies } from '../external-dependencies/external-dependencies';
import { HorizontalMenuSetup } from '../horizontal-menu';
import { AddressMenu } from '../address-menu/address-menu';
import { FAVORITE_ADDRESS_HINT_SHOWN } from 'common/constants/local-storage-constants';
import { sendShowFavoriteAddressHintEvent } from '../../../../utils/ga-events';
import { getFavoriteAddressHintVariant } from '../city-picker/get-favorite-address-hint';

type Props = {
  model?: HeaderModelData;
  disableAppsflyer?: boolean;
  disableSticky?: boolean;
  disableHorizontalMenu?: boolean;
  withoutRegionModalButton?: boolean;
};

export const HeaderLayout = ({
  model = {} as HeaderModelData,
  disableAppsflyer,
  disableSticky,
  withoutRegionModalButton,
  disableHorizontalMenu,
}: Props) => {
  const { cookies, isBot, deviceType } = useStoreon<State>('cookies', 'isBot', 'deviceType');
  const isAuth = isUserAuth(cookies);
  const headerContainerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const canShowFavoriteAddressHint = isFavoriteAddressEnabled(cookies);
  const isRegionVariant = getFavoriteAddressHintVariant(cookies) === 'REGION';

  const headerContainerHeight = headerContainerRef?.current?.clientHeight || 0;
  const isStickyVisible = useStickyHeader(headerRef, isVisible, !!disableSticky, 10, headerContainerHeight);
  const enableLoyaltyRegistrationBanner = isEnableLoyaltyRegistrationBanner(model?.experiments);

  useLoyaltyAccount({
    enabled: isAuth && enableLoyaltyRegistrationBanner,
  });
  useAutoShowLoyaltyRegistrationModal();
  const favoriteAddressView = useFavoriteAddress();
  const { enabled } = favoriteAddressView;

  const hamburgerMenuState = useHamburgerMenuState();
  const regionsMenuState = useCityPickerState();
  const { hintShow, onHintOpen } = regionsMenuState;

  useEffect(() => {
    if (!canShowFavoriteAddressHint || localStorage.getItem(FAVORITE_ADDRESS_HINT_SHOWN)) return;

    sendShowFavoriteAddressHintEvent(isRegionVariant);
    localStorage.setItem(FAVORITE_ADDRESS_HINT_SHOWN, 'true');

    if (!hintShow) onHintOpen();
  }, [hintShow, onHintOpen, canShowFavoriteAddressHint, isRegionVariant]);

  return (
    <>
      <ExternalDependencies disableAppsflyerByConsumer={disableAppsflyer} />
      <div ref={headerContainerRef}>
        <div className={headerFullScreenClassName}>
          <HeaderContent
            model={model}
            isBot={isBot}
            isStickyVisible={isStickyVisible}
            deviceType={deviceType}
            withoutRegionModalButton={withoutRegionModalButton}
            favoriteAddressView={favoriteAddressView}
          />
        </div>
        <StickyContainer isBot={isBot} disableSticky={!!disableSticky} ref={headerRef}>
          <HeaderContent
            model={model}
            isBot={isBot}
            isSticky
            isStickyVisible={isStickyVisible}
            deviceType={deviceType}
            withoutRegionModalButton={withoutRegionModalButton}
            favoriteAddressView={favoriteAddressView}
          />
        </StickyContainer>
        <MobilePartHeader model={model} deviceType={deviceType} />
      </div>
      <MainMenu model={model} {...hamburgerMenuState} />
      <AddressMenu {...regionsMenuState} visible={enabled} model={model} />
      <RegionsMenu
        model={model}
        show={enabled ? regionsMenuState.regionsShow : regionsMenuState.show}
        onMenuClose={enabled ? regionsMenuState.onRegionsMenuClose : regionsMenuState.onMenuClose}
        direction={enabled ? 'right' : 'left'}
      />
      <LoyaltyRegistrationModal />
      <HorizontalMenuSetup
        disableHorizontalMenu={disableHorizontalMenu}
        model={model.horizontalMenu}
        deviceType={deviceType}
      />
    </>
  );
};

type MobilePartHeaderProps = {
  model: HeaderModelData;
  deviceType: DeviceType;
};

const MobilePartHeader = ({ model, deviceType }: MobilePartHeaderProps) => {
  const isCheckout = useIsCheckout();

  if (deviceType === 'desktop' || isCheckout) {
    return null;
  }

  return <MenuMobile model={model} />;
};

export const HeaderComponent = withContextSetup(withModel(HeaderLayout));
